import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import moment from "moment";
import { FormEventHandler } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { TriggerFunction } from "api/common/types/response.types";
import {
  ProductUpdate,
  ProductUpdateCreatePayload,
  ProductUpdateEditPayload,
  ProductUpdatePcm,
} from "api/memberPortal/product/productUpdate/productUpdate.types";
import { useAppForm } from "components/Forms/AppForm";

import SeveritySelect from "../components/updateForm/components/SeverityOption/SeveritySelect";
import { defaultProductUpdateValues } from "../components/updateForm/defaultValues";
import { ProductUpdateSchema } from "../components/updateForm/ProductUpdate.schema";

interface ProductUpdateModalProps<T> {
  currentUpdate?: T;
  editMode?: boolean;
  onEdit: TriggerFunction<ProductUpdateEditPayload, T>;
  onCreate: TriggerFunction<ProductUpdateCreatePayload, T>;
}

const ProductUpdateBaseModal = <T extends ProductUpdate | ProductUpdatePcm>({
  editMode,
  onEdit,
  onCreate,
  currentUpdate,
}: ProductUpdateModalProps<T>) => {
  const navigate = useNavigate();

  const { id: parentId, updateId } = useParams();

  const updateForm = useAppForm({
    defaultValues: currentUpdate ?? defaultProductUpdateValues,
    validators: {
      onSubmit: ProductUpdateSchema,
    },

    onSubmit: async ({ value: body }) => {
      if (!parentId) return;

      if (editMode && updateId) {
        await onEdit({ parentId, updateId, body });
      } else {
        await onCreate({ parentId, body });
      }

      navigate("..");
    },
  });

  const handleCreate: FormEventHandler = (e) => {
    e.preventDefault();
    updateForm.handleSubmit();
  };

  return (
    <Dialog open>
      <form onSubmit={handleCreate}>
        <DialogTitle>Post Listing Updates</DialogTitle>

        <DialogContent>
          <updateForm.AppForm>
            <updateForm.FormSection title="Update Type" columns={1}>
              <Box display="flex" gap={2}>
                <SeveritySelect form={updateForm} />
                <updateForm.AppField name="title">
                  {(field) => <field.TextInput label="Title" />}
                </updateForm.AppField>
              </Box>
            </updateForm.FormSection>

            <updateForm.AppField name="content">
              {(field) => <field.HtmlEditor label="MESSAGE" />}
            </updateForm.AppField>

            {currentUpdate && (
              <Typography variant="body2" pt={1} textAlign="right">
                Last Edited: {currentUpdate.author} on{" "}
                {moment(currentUpdate.lastModified).format("LLL")}
              </Typography>
            )}
          </updateForm.AppForm>
        </DialogContent>

        <DialogActions>
          <Button component={Link} variant="contained" color="error" to="..">
            Cancel
          </Button>

          <Button variant="contained" color="primary" type="submit">
            {editMode ? "Edit" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
export default ProductUpdateBaseModal;
